import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Alert,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useGetProductCertQuery } from '@/services';
import CertificateVerification from '../certificate-verification';

interface EditCertificateModalProps {
  open: boolean;
  onClose: () => void;
  certificateId: string | null;
  onSuccess?: () => void;
}

const EditCertificateModal: React.FC<EditCertificateModalProps> = ({
  open,
  onClose,
  certificateId,
  onSuccess,
}) => {
  const [editedData, setEditedData] = useState<CertificateExtractedData | null>(null);

  const {
    data: certificate,
    isLoading,
    error,
  } = useGetProductCertQuery(
    { id: certificateId! },
    { skip: !certificateId || !open, refetchOnMountOrArgChange: true }
  );

  useEffect(() => {
    if (open && certificate?.extractedData) {
      setEditedData(certificate.extractedData);
    }
  }, [certificate, open]);

  useEffect(() => {
    if (!open) {
      setEditedData(null);
    }
  }, [open]);

  const handleSave = () => {
    // The verification component handles the actual save
    onSuccess?.();
    onClose();
  };

  const handleClose = () => {
    setEditedData(null);
    onClose();
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          minHeight: "600px",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box>
          <Typography variant="h6">Edit Certificate</Typography>
          <Typography variant="body2" color="text.secondary">
            Modify the certificate data and save your changes
          </Typography>
        </Box>
        
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error">
            Failed to load certificate data. Please try again.
          </Alert>
        )}

        {!isLoading && !error && editedData && certificate && (
          <CertificateVerification
            data={editedData}
            formType={certificate.formType || 'USMCA'}
            onSave={handleSave}
            certificateId={certificateId!}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Make your changes and click "Save & Continue" to update the certificate
        </Typography>
      </DialogActions>
    </Dialog>
  );
};

export default EditCertificateModal;